import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';
import type { SessionSummary } from '@voice-assistant/shared';
import { useComposerStore, useBusySessionIds, type ComposerStore } from './composerStore';

// ─── Entry type ───────────────────────────────────────────────────────────────
// A session-list entry IS a SessionSummary. The store carries every server
// field verbatim so renderers (SessionListView, ProjectChips, agent-pulse,
// rolling-signpost, etc.) can read whatever they need without a manual mapping
// layer. Convenience aliases live in selectors.
export type SessionEntry = SessionSummary;

// ─── Store interface ──────────────────────────────────────────────────────────
//
// Origin-keyed by sessionId. The session that ORIGINATED the entry is the key;
// re-using an id with a different originator is not a supported case (the
// server treats sessionId as a global identifier).
//
// `reconcile` is the AUTHORITATIVE server snapshot — it REPLACES the canonical
// set and prunes entries absent from the list (mirrors the current
// SESSIONS_LIST open-tab cleanup in useSocket.ts). Single-entry actions
// (`upsert` / `rename` / `remove` / `setSummary` / `setMeta`) handle deltas
// between reconciles.
//
// NO consumers wired in Phase 0 — additive only.

export interface SessionListStore {
  sessions: Map<string, SessionEntry>;
  /**
   * True once the FIRST authoritative server snapshot (`reconcile`, even of an EMPTY list)
   * has landed. NOT persisted: a reload starts unsynced until the server answers — the
   * desktop-home rail renders its loading skeleton from `!synced` and its designed empty
   * state from `synced && sessions.size === 0`.
   */
  synced: boolean;
  /** Authoritative snapshot — REPLACES the canonical set and prunes absent entries. */
  reconcile: (list: SessionSummary[]) => void;
  /** Upsert by id — new entry or merge into existing (server delta wins on collision). */
  upsert: (entry: SessionSummary) => void;
  /** Single-field rename (display name only). */
  rename: (id: string, title: string) => void;
  /** Remove from canonical set (delete/clear). No-op if id absent. */
  remove: (id: string) => void;
  /** Set the rollingSignpost summary on an entry. No-op if id absent. */
  setSummary: (id: string, summary: string) => void;
  /** Patch numTurns / totalCostUsd on an entry. No-op if id absent. */
  setMeta: (id: string, meta: { numTurns?: number; totalCostUsd?: number | null }) => void;
  /**
   * Partial-merge patch — apply a field subset to an existing entry. No-op if
   * id absent. Used by the Phase 3 per-site cut-over in `useSocket.ts` to
   * mirror small WsEvent-driven mutations (CONTEXT_TAG_UPDATED, PROJECTS_DETECTED,
   * CONTEXT_RESET, tuning updates, pin position, …) without reconstructing a
   * full SessionSummary at every callsite.
   */
  patch: (id: string, patch: Partial<SessionEntry>) => void;
}

// ─── Map serializer ───────────────────────────────────────────────────────────
// JSON.stringify/parse do not round-trip Map natively. These replacer/reviver
// functions let the persist middleware serialize the sessions Map correctly.
// (Identical shape to composerStore's mapAwareStorage — covered by
// SE-SLST-STORE-SHAPE round-trip assertion.)

function mapReplacer(_key: string, value: unknown): unknown {
  if (value instanceof Map) {
    return { __type: 'Map', entries: [...value.entries()] };
  }
  return value;
}

function mapReviver(_key: string, value: unknown): unknown {
  if (
    value !== null &&
    typeof value === 'object' &&
    (value as Record<string, unknown>).__type === 'Map'
  ) {
    return new Map(
      (value as { entries: [string, SessionEntry][] }).entries,
    );
  }
  return value;
}

const mapAwareStorage = createJSONStorage(() => localStorage, {
  replacer: mapReplacer,
  reviver: mapReviver,
});

// ─── Store creation ───────────────────────────────────────────────────────────

export const useSessionListStore = create<SessionListStore>()(
  persist(
    (set): SessionListStore => ({
      sessions: new Map<string, SessionEntry>(),
      synced: false,

      reconcile: (list) => {
        set(() => {
          const sessions = new Map<string, SessionEntry>();
          for (const entry of list) {
            sessions.set(entry.id, entry);
          }
          return { sessions, synced: true };
        });
      },

      upsert: (entry) => {
        set((state) => {
          const sessions = new Map(state.sessions);
          const existing = sessions.get(entry.id);
          sessions.set(entry.id, existing ? { ...existing, ...entry } : entry);
          return { sessions };
        });
      },

      rename: (id, title) => {
        set((state) => {
          const sessions = new Map(state.sessions);
          const existing = sessions.get(id);
          if (!existing) return {};
          sessions.set(id, { ...existing, name: title });
          return { sessions };
        });
      },

      remove: (id) => {
        set((state) => {
          if (!state.sessions.has(id)) return {};
          const sessions = new Map(state.sessions);
          sessions.delete(id);
          return { sessions };
        });
      },

      setSummary: (id, summary) => {
        set((state) => {
          const existing = state.sessions.get(id);
          if (!existing) return {};
          const sessions = new Map(state.sessions);
          sessions.set(id, { ...existing, rollingSignpost: summary });
          return { sessions };
        });
      },

      patch: (id, patch) => {
        set((state) => {
          const existing = state.sessions.get(id);
          if (!existing) return {};
          const sessions = new Map(state.sessions);
          sessions.set(id, { ...existing, ...patch });
          return { sessions };
        });
      },

      setMeta: (id, meta) => {
        set((state) => {
          const existing = state.sessions.get(id);
          if (!existing) return {};
          const sessions = new Map(state.sessions);
          sessions.set(id, {
            ...existing,
            ...(meta.numTurns !== undefined ? { numTurns: meta.numTurns } : {}),
            ...(meta.totalCostUsd !== undefined ? { totalCostUsd: meta.totalCostUsd } : {}),
          });
          return { sessions };
        });
      },
    }),
    {
      name: 'va-session-list-v1',
      storage: mapAwareStorage,
      // `synced` is session-lifetime truth, never persisted (a stale `true` would skip the
      // loading state and the post-auth list request below).
      partialize: (state) => ({ sessions: state.sessions }) as SessionListStore,
    },
  ),
);

/**
 * Pure predicate: must the client (re)request the session list? True until the first
 * authoritative snapshot lands. The boot-time LIST_SESSIONS can be dropped server-side
 * (the gateway authenticates AFTER the INITIALIZE ack, so an early request is answered
 * with an auth ERROR and no SESSIONS_LIST); SESSIONS_RECONCILE is emitted only AFTER auth
 * completes, so the client re-requests the list there while still unsynced.
 */
export function needsSessionListSync(state: Pick<SessionListStore, 'synced'>): boolean {
  return !state.synced;
}

// ─── Pure selectors (testable; no React) ─────────────────────────────────────
//
// These are pure functions of store state — exactly the contract
// SE-SLST-DERIVED-PURE locks. Hooks below subscribe via useShallow and forward
// to the pure variants so behavior under React == behavior in scenario tests.

/** Sort entries DESC by updatedAt (recent first) or ASC by name. Stable on equal keys via id tie-break. */
export function selectSortedSessions(
  store: SessionListStore,
  sortBy: 'recent' | 'name',
): SessionEntry[] {
  const entries = [...store.sessions.values()];
  if (sortBy === 'name') {
    entries.sort((a, b) => {
      const aName = a.name ?? '';
      const bName = b.name ?? '';
      if (aName < bName) return -1;
      if (aName > bName) return 1;
      return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
    });
  } else {
    entries.sort((a, b) => {
      const aT = a.updatedAt ?? '';
      const bT = b.updatedAt ?? '';
      if (aT > bT) return -1;
      if (aT < bT) return 1;
      return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
    });
  }
  return entries;
}

/**
 * The single "is this an AGENT session (not the operator's own)?" predicate. Collapsed to one
 * place so every consumer — the You/Agent list split, the active-agent count, the agent badge, and
 * the mobile read-only composer gate — agrees ([[predicate-drift]]): a source added here is an
 * agent session EVERYWHERE at once, never in the tab but not the composer-gate.
 *
 * - `autonomous`: a VA-orchestrated autonomous run.
 * - `host-cli`: a READ-ONLY projection of a host-typed `claude` (host-cli-session-consolidation
 *   §3 B4). It belongs in the Agent tab, and — because it is view-only (§2.7 firewall) — the mobile
 *   composer is hidden for it, the frontend complement of the gateway's `refuseHostCliIntake`.
 */
export function isAgentSource(source: string | null | undefined): boolean {
  return source === 'autonomous' || source === 'host-cli';
}

/** Listed user sessions: status !== 'cleared' AND NOT an agent source. */
export function selectUserSessions(store: SessionListStore): SessionEntry[] {
  const out: SessionEntry[] = [];
  store.sessions.forEach((entry) => {
    if (entry.status !== 'cleared' && !isAgentSource(entry.source)) out.push(entry);
  });
  return out;
}

/** Listed agent sessions: status !== 'cleared' AND an agent source (autonomous | host-cli). */
export function selectAgentSessions(store: SessionListStore): SessionEntry[] {
  const out: SessionEntry[] = [];
  store.sessions.forEach((entry) => {
    if (entry.status !== 'cleared' && isAgentSource(entry.source)) out.push(entry);
  });
  return out;
}

/** Listed session ids (status !== 'cleared'). Used by the "any busy listed?" selector. */
export function selectListedSessionIds(store: SessionListStore): string[] {
  const out: string[] = [];
  store.sessions.forEach((entry, id) => {
    if (entry.status !== 'cleared') out.push(id);
  });
  return out;
}

// ─── Cross-store pure functions ──────────────────────────────────────────────
//
// Both selectors mirror the busy-set construction in
// `composerStore.useBusySessionIds` so the SE test the EXACT same logic the
// hook ships. Extracting these as pure functions (no React) is what lets the
// Phase 1 simulator drive them through synthetic scenarios without needing a
// React render — the hook variants below are thin wrappers.

/** Compute the busy set from a ComposerStore snapshot. Mirrors `useBusySessionIds`. */
export function computeBusySet(composer: ComposerStore): Set<string> {
  const ids = new Set<string>();
  composer.sessions.forEach((region, sid) => {
    if (
      region.outbound.some(
        (e) =>
          e.mode === 'submitting' ||
          e.mode === 'queued' ||
          e.mode === 'awaiting_stream' ||
          e.mode === 'streaming',
      )
    ) {
      ids.add(sid);
    }
  });
  composer.orchestrationGated?.forEach((sid) => ids.add(sid));
  return ids;
}

/** Pure: count of agent sessions intersected with the composer busy set. */
export function computeActiveAgentCount(
  slst: SessionListStore,
  composer: ComposerStore,
): number {
  const busy = computeBusySet(composer);
  if (busy.size === 0) return 0;
  let count = 0;
  for (const entry of slst.sessions.values()) {
    if (
      entry.status !== 'cleared' &&
      isAgentSource(entry.source) &&
      busy.has(entry.id)
    ) {
      count++;
    }
  }
  return count;
}

/**
 * Pure: count of NON-autonomous (interactive/relay) sessions intersected with
 * the composer busy set. Symmetric to `computeActiveAgentCount` — the "In
 * Progress" affordance counts only YOUR sessions, never agent ones.
 */
export function computeActiveUserCount(
  slst: SessionListStore,
  composer: ComposerStore,
): number {
  const busy = computeBusySet(composer);
  if (busy.size === 0) return 0;
  let count = 0;
  for (const entry of slst.sessions.values()) {
    if (
      entry.status !== 'cleared' &&
      !isAgentSource(entry.source) &&
      busy.has(entry.id)
    ) {
      count++;
    }
  }
  return count;
}

/** Pure: true iff any LISTED (status !== 'cleared') session is in the composer busy set. */
export function computeAnyBusyListed(
  slst: SessionListStore,
  composer: ComposerStore,
): boolean {
  const busy = computeBusySet(composer);
  if (busy.size === 0) return false;
  for (const entry of slst.sessions.values()) {
    if (entry.status !== 'cleared' && busy.has(entry.id)) return true;
  }
  return false;
}

// ─── React hooks (consumer surface) ──────────────────────────────────────────

export function useUserSessions(): SessionEntry[] {
  return useSessionListStore(useShallow(selectUserSessions));
}

export function useAgentSessions(): SessionEntry[] {
  return useSessionListStore(useShallow(selectAgentSessions));
}

/**
 * Cross-store: count of agent sessions that are also currently busy in the
 * composer machine. The desync bug fixed by this row — agents-active pill
 * no longer sees a different list than the agent-pulse count.
 *
 * Thin wrapper over `computeActiveAgentCount` — the pure function is the
 * SE-locked contract, this hook just subscribes both stores so the count
 * re-renders when either changes.
 */
export function useActiveAgentCount(): number {
  // Subscribe to both stores via dependency hooks so the consumer re-renders
  // when either the listed agents or the composer busy set changes.
  const agentSessions = useAgentSessions();
  const busy = useBusySessionIds();
  const busySet = new Set(busy);
  let count = 0;
  for (const s of agentSessions) {
    if (busySet.has(s.id)) count++;
  }
  return count;
}

/**
 * Cross-store: count of YOUR (non-autonomous) sessions that are also currently
 * busy in the composer machine. Symmetric to `useActiveAgentCount`; thin
 * wrapper over `computeActiveUserCount` — drives the "In Progress" filter
 * affordance in SessionListView.
 */
export function useActiveUserCount(): number {
  const userSessions = useUserSessions();
  const busy = useBusySessionIds();
  const busySet = new Set(busy);
  let count = 0;
  for (const s of userSessions) {
    if (busySet.has(s.id)) count++;
  }
  return count;
}

/**
 * Cross-store: true iff any LISTED session (status !== 'cleared') is busy in
 * the composer machine. A busy session that's not in the list doesn't count.
 * Thin wrapper over `computeAnyBusyListed` — see that function for the contract.
 */
export function useAnyBusyListed(): boolean {
  const listed = useSessionListStore(useShallow(selectListedSessionIds));
  const busy = useBusySessionIds();
  if (busy.length === 0 || listed.length === 0) return false;
  const listedSet = new Set(listed);
  return busy.some((id) => listedSet.has(id));
}

/**
 * All sessions as a stable array. Drop-in replacement for the legacy
 * `sessions: SessionSummary[]` state in `useSocket.ts` — after Phase 4,
 * useSocket's return value derives from this hook instead of holding its
 * own useState. useShallow keeps the array reference stable between renders
 * when content doesn't change.
 */
export function useAllSessions(): SessionEntry[] {
  return useSessionListStore(useShallow((state) => [...state.sessions.values()]));
}

/**
 * Aggregate per-session meta (numTurns / totalCostUsd / summary) keyed by
 * sessionId — the shape the legacy `sessionMeta` state and its downstream
 * consumers (ChatArea, ConsoleView) expect. Derived from the store entries
 * so a single Map is the source of truth.
 */
export type SessionMetaValue = { numTurns?: number; totalCostUsd?: number; summary?: string };
export type AggregateSessionMeta = Record<string, SessionMetaValue>;

// Per-entry meta memo. `useShallow` compares the aggregate record only one
// level deep — it does Object.is on each `record[id]` value. If we rebuilt
// those nested value objects on every selector call, every comparison would be
// false → the snapshot would never be referentially stable → React's
// useSyncExternalStore re-renders without bound → the main thread saturates and
// the page renders once then goes inert (prod regression, 2026-05-31).
// Keying the memo on the SessionEntry reference makes the nested value object
// stable until the entry itself changes (store actions always create a new
// entry object on mutation), which keeps the shallow comparison meaningful.
const metaValueCache = new WeakMap<SessionEntry, SessionMetaValue>();

function entryMeta(entry: SessionEntry): SessionMetaValue {
  const cached = metaValueCache.get(entry);
  if (cached) return cached;
  const value: SessionMetaValue = {
    ...(entry.numTurns !== undefined ? { numTurns: entry.numTurns } : {}),
    ...(entry.totalCostUsd !== undefined && entry.totalCostUsd !== null
      ? { totalCostUsd: typeof entry.totalCostUsd === 'string' ? Number(entry.totalCostUsd) : entry.totalCostUsd }
      : {}),
    ...(entry.rollingSignpost ? { summary: entry.rollingSignpost } : {}),
  };
  metaValueCache.set(entry, value);
  return value;
}

/** Pure: aggregate per-session meta keyed by id. Nested values are reference-stable per entry. */
export function selectAllSessionMeta(store: SessionListStore): AggregateSessionMeta {
  const out: AggregateSessionMeta = {};
  store.sessions.forEach((entry, id) => {
    out[id] = entryMeta(entry);
  });
  return out;
}

export function useAllSessionMeta(): AggregateSessionMeta {
  return useSessionListStore(useShallow(selectAllSessionMeta));
}

// Re-export useComposerStore so SE behavioral tests have a single import surface
// (the cross-store selectors above pull busy from composerStore — this just
// makes that wiring discoverable from the store file). Tree-shaken in builds.
export { useComposerStore };
